<template>
  <Block badge-class="bg-blue-50" :emoji="emoji" :title="title">
    <template v-if="totalRetributionsAmount > 0" #actions>
      <div class="hidden sm:flex flex-col items-end text-right">
        <span class="uppercase text-xs tracking-wide text-gray-400 font-semibold">Prime équipe</span>
        <div class="inline-flex items-center">
          <span class="text-lg font-bold text-gray-900">
            {{ formatPrice(totalRetributionsAmount) }}
          </span>
        </div>
      </div>
    </template>

    <div
      class="overflow-x-auto max-h-56 sm:max-h-48 overflow-y-auto border-t border-gray-100"
      v-if="formattedSellersWithRetributionRanking.length > 0"
    >
      <Table
        :columns="columns"
        :data="formattedSellersWithRetributionRanking"
        :isRowClickable="showRetributionDetails"
        @rowClick="viewRetributions"
      >
        <template #rank="{ row }">
          <div
            class="w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs"
            :class="getMedalClass(row.rank)"
          >
            {{ row.rank }}
          </div>
        </template>

        <template #name="{ row }">
          <div class="flex flex-col sm:block">
            <h4 class="text-sm font-semibold text-gray-900 flex items-center gap-1 leading-none min-w-0">
              <span class="truncate" :title="row.name">{{ getFirstName(row.name) }}</span>
              <span v-if="row.networkRank != null" class="text-xs font-normal text-gray-400">
                <!-- prettier-ignore -->
                <span>#{{ row.networkRank }}<sup>{{ ordinalSuffix(row.networkRank) }}</sup></span>
                {{ $t("roadmap.network") }}
              </span>
            </h4>
            <p
              v-if="showStoreSalesChannel && hasMultipleStores && row.storeSalesChannel"
              class="text-xs text-gray-400 leading-tight mt-0.5"
            >
              {{ row.storeSalesChannel }}
            </p>
            <!-- Informations supplémentaires sur mobile -->
            <div class="flex items-center gap-3 mt-1 sm:hidden">
              <p class="text-xs text-gray-600">
                {{ $t("roadmap.sales", row.sales) }}
                <span v-if="row.leadsWaiting > 0" class="text-xs text-gray-400">
                  (+{{ row.leadsWaiting }} en attente)
                </span>
              </p>
              <p
                v-if="showRetributionDetails"
                class="text-xs text-gray-600 cursor-pointer underline"
                @click.stop="viewRetributions(row)"
              >
                🔍
              </p>
              <div
                v-if="row.sellerRetributionAmount && totalRetributionsAmount > 0"
                class="flex flex-col items-end text-right"
              >
                <span class="text-sm font-semibold text-gray-900">
                  {{ row.sellerRetributionAmount }}
                </span>
              </div>
            </div>
          </div>
        </template>

        <template #retributions="{ row }">
          <p class="hidden sm:block text-xs text-gray-600 cursor-pointer underline" @click.stop="viewRetributions(row)">
            🔍
          </p>
        </template>

        <template #sales="{ row }">
          <p class="hidden sm:block text-sm text-gray-600">
            {{ $t("roadmap.sales", row.sales) }}
            <span v-if="row.leadsWaiting > 0" class="text-xs text-gray-400">(+{{ row.leadsWaiting }} en attente)</span>
          </p>
        </template>

        <template #sellerRetributionAmount="{ row }">
          <div v-if="row.sellerRetributionAmount" class="hidden sm:flex flex-col items-end text-right">
            <span class="text-sm font-bold text-gray-900">
              {{ row.sellerRetributionAmount }}
            </span>
          </div>
        </template>
      </Table>
    </div>
    <div v-else class="flex items-center justify-center p-4 border-t border-gray-100">
      <p class="text-sm text-gray-600">
        Réalisez votre première vente du mois pour voir votre classement s’animer. Chaque vente compte — à vous de jouer
        !
      </p>
    </div>
  </Block>
</template>

<script>
import { Table, Block, formatPrice, computeCompetitionRanking } from "@estaly/ui";
import { mapGetters } from "vuex";

export default {
  components: {
    Block,
    Table,
  },
  props: {
    sellersWithRetributionRanking: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      default: "Classement magasin",
    },
    emoji: {
      type: String,
      default: "🏪",
    },
    showRetributionDetails: {
      type: Boolean,
      default: false,
    },
    showStoreSalesChannel: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapGetters("store", ["hasMultipleStores"]),

    formattedSellersWithRetributionRanking() {
      // plan_sales mode returns totalRetributions, gross_sales mode returns salesCount
      const salesOf = (item) => item.totalRetributions ?? item.salesCount ?? 0;
      const sorted = [...this.sellersWithRetributionRanking].sort((a, b) => salesOf(b) - salesOf(a));

      return computeCompetitionRanking(sorted, salesOf).map((sellerWithRetribution) => ({
        rank: sellerWithRetribution.rank,
        sellerId: sellerWithRetribution.seller.id,
        name: sellerWithRetribution.seller.name,
        storeSalesChannel: sellerWithRetribution.seller.storeSalesChannel,
        sales: salesOf(sellerWithRetribution),
        leadsWaiting: sellerWithRetribution.leadsWaitingForConfirmation || 0,
        sellerRetributionAmount: formatPrice(sellerWithRetribution.totalSellerAmount),
        networkRank: sellerWithRetribution.networkRank,
        totalNetworkSellers: sellerWithRetribution.totalNetworkSellers,
      }));
    },

    totalRetributionsAmount() {
      return (this.sellersWithRetributionRanking ?? []).reduce(
        (total, s) => total + Number(s.totalSellerAmount || 0),
        0,
      );
    },
    columns() {
      const columns = [
        { label: null, field: "rank", slot: "rank", widthClass: "w-12" },
        { label: null, field: "name", slot: "name" },
        { label: null, field: "sales", slot: "sales", numeric: true, widthClass: "w-20" },
      ];

      const extraColumns = [
        this.showRetributionDetails && {
          label: null,
          field: "retributions",
          slot: "retributions",
          widthClass: "w-2",
        },
        this.totalRetributionsAmount > 0 && {
          label: null,
          field: "sellerRetributionAmount",
          slot: "sellerRetributionAmount",
          numeric: true,
          widthClass: "w-20",
        },
      ];

      return [...columns, ...extraColumns.filter(Boolean)];
    },
  },
  methods: {
    formatPrice,
    ordinalSuffix(rank) {
      const category = new Intl.PluralRules(this.$i18n.locale, { type: "ordinal" }).select(rank);
      return this.$t(`roadmap.ordinal_suffix.${category}`);
    },
    getFirstName(fullName) {
      if (!fullName) return "";
      return fullName.split(" ")[0];
    },
    getMedalClass(ranking) {
      if (ranking === 0) return;

      const classes = [
        "bg-linear-to-r/srgb from-yellow-400 to-yellow-500 text-white",
        "bg-linear-to-r/srgb from-gray-300 to-gray-400 text-white",
        "bg-linear-to-r/srgb from-orange-400 to-orange-800 text-white",
      ];
      return classes[ranking - 1] || "bg-gray-100 text-gray-600";
    },
    viewRetributions(row) {
      this.$emit("viewRetributions", row.sellerId);
    },
  },
};
</script>
